import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  HStack,
  Box,
  Badge,
  Icon,
  useColorModeValue,
  Divider,
} from '@chakra-ui/react';
import { Briefcase, User, Clock, Eye } from 'lucide-react';
import { Issue, User as UserType, Rock } from '../../types';
import {
  calculateDaysOpen,
  formatDate,
  formatDaysOpen,
  getIssueRockName,
  getIssueAssignee,
} from '../issues.util';
import { ResolveIssueModal } from '../../Rocks/components/ResolveIssueModal';
import { useDisclosure } from '@chakra-ui/react';

interface IssueDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  issue: Issue | null;
  users: UserType[];
  rocks: Rock[];
  onEdit: (issue: Issue) => void;
  onResolve: (issueId: string, resolutionNotes: string) => Promise<void>;
  currentUserId?: string;
}

/**
 * Issue Details Modal Component
 * Displays issue information in read-only view with actions
 */
export function IssueDetailsModal({
  isOpen,
  onClose,
  issue,
  users,
  rocks,
  onEdit,
  onResolve,
  currentUserId,
}: IssueDetailsModalProps) {
  const {
    isOpen: isResolveModalOpen,
    onOpen: onResolveModalOpen,
    onClose: onResolveModalClose,
  } = useDisclosure();

  const textColor = useColorModeValue('gray.900', 'gray.100');
  const textColorLight = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  if (!issue) return null;

  const getStatusColor = (status: string) => {
    if (status === 'solved') return 'green';
    if (status === 'in-progress') return 'yellow';
    return 'green'; // Open status is green
  };

  const getStatusLabel = (status: string) => {
    if (status === 'in-progress') return 'In Progress';
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const assignee = getIssueAssignee(issue, users);
  const rockName = getIssueRockName(issue, rocks);
  const daysOpen = calculateDaysOpen(issue.firstOpenedDate);
  const creator = users.find((u) => u.id === issue.ownerId);
  const isResolved = issue.status === 'solved';

  const handleResolveClick = () => {
    onResolveModalOpen();
  };

  const handleConfirmResolve = async (resolutionNotes: string) => {
    await onResolve(issue.id, resolutionNotes);
    onResolveModalClose();
    onClose();
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Issue Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={4}>
              {/* Title */}
              <Text fontSize="2xl" fontWeight="bold" color={textColor}>
                {issue.name}
              </Text>

              {/* Status */}
              <HStack spacing={2}>
                <Box
                  w={3}
                  h={3}
                  borderRadius="full"
                  bg={`${getStatusColor(issue.status)}.500`}
                />
                <Badge colorScheme={getStatusColor(issue.status)}>
                  {getStatusLabel(issue.status)}
                </Badge>
              </HStack>

              <Divider />

              {/* Linked to */}
              <HStack spacing={2}>
                {issue.isCompany ? (
                  <>
                    <Icon as={Briefcase} w={4} h={4} color={textColorLight} />
                    <Text fontSize="sm" color={textColor}>
                      Linked to: <strong>Company-wide</strong>
                    </Text>
                  </>
                ) : rockName ? (
                  <>
                    <Icon as={Eye} w={4} h={4} color="blue.500" />
                    <Text fontSize="sm" color={textColor}>
                      Linked to: <strong>{rockName}</strong>
                    </Text>
                  </>
                ) : (
                  <Text fontSize="sm" color={textColorLight}>
                    Not linked to a rock
                  </Text>
                )}
              </HStack>

              {/* Assigned to */}
              <HStack spacing={2}>
                <Icon as={User} w={4} h={4} color={textColorLight} />
                <Text fontSize="sm" color={textColor}>
                  Assigned to:{' '}
                  <strong>{assignee ? assignee.name : 'Unassigned'}</strong>
                </Text>
              </HStack>

              {/* Created by */}
              <HStack spacing={2}>
                <Icon as={User} w={4} h={4} color={textColorLight} />
                <Text fontSize="sm" color={textColor}>
                  Created by:{' '}
                  <strong>{creator ? creator.name : 'Unknown'}</strong> on{' '}
                  {formatDate(issue.createdAt)}
                </Text>
              </HStack>

              {/* Days open */}
              <HStack spacing={2}>
                <Icon as={Clock} w={4} h={4} color={textColorLight} />
                <Text fontSize="sm" color={textColor}>
                  Days open: <strong>{formatDaysOpen(daysOpen)}</strong>
                </Text>
              </HStack>

              <Divider />

              {/* Description */}
              <Box>
                <Text fontSize="sm" fontWeight="medium" color={textColor} mb={2}>
                  Description:
                </Text>
                <Text fontSize="sm" color={textColorLight} whiteSpace="pre-wrap">
                  {issue.description}
                </Text>
              </Box>

              {/* Resolution Notes (if resolved) */}
              {isResolved && issue.resolutionNotes && (
                <Box>
                  <Text fontSize="sm" fontWeight="medium" color={textColor} mb={2}>
                    Resolution Notes:
                  </Text>
                  <Text fontSize="sm" color={textColorLight} whiteSpace="pre-wrap">
                    {issue.resolutionNotes}
                  </Text>
                </Box>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <HStack spacing={3} width="100%" justifyContent="flex-end">
              <Button variant="ghost" onClick={onClose}>
                Close
              </Button>
              <Button
                variant="outline"
                colorScheme="blue"
                onClick={() => onEdit(issue)}
              >
                Edit Issue
              </Button>
              {!isResolved && (
                <Button colorScheme="blue" onClick={handleResolveClick}>
                  Mark Resolved
                </Button>
              )}
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {issue && (
        <ResolveIssueModal
          isOpen={isResolveModalOpen}
          onClose={onResolveModalClose}
          onConfirm={handleConfirmResolve}
          issueTitle={issue.name}
        />
      )}
    </>
  );
}

